// useForm-lite: controlled values, dirty tracking, zod validation, server `fields` errors mapping,
// double-submit protection and step-up auth. Pair with <Field error={form.error('x')}> and
// <StickySaveBar dirty={form.dirty} saving={form.submitting} onSave={form.submit} onDiscard={() => form.reset()} />.
import { useCallback, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import type { ZodType } from 'zod'
import { ApiError } from '@/lib/api'
import { withReauth } from './reauth'
import { toast } from './toast'
import { blockOnInvalidDraft } from './draft'

type AnyRecord = Record<string, any>
export { getPath, setPath, deepEqual, zodErrors, splitFieldErrors, ROOT_ERROR_KEYS } from './form-utils'
import { getPath, setPath, deepEqual, zodErrors, splitFieldErrors } from './form-utils'

export interface UseFormOptions<T extends AnyRecord, R> {
  initial: T
  /** Client-side validation (zod). Errors show after the first submit, then live as the user fixes them. */
  schema?: ZodType
  /** Persist. Throw (e.g. ApiError with `fields`) to show errors; the resolved value is passed to onSuccess. Wrapped in withReauth. */
  onSubmit: (values: T) => Promise<R> | R
  onSuccess?: (result: R, values: T) => void
  /** Toast shown on success (default none). */
  successMessage?: string
  /** After success, make the submitted values the new clean baseline (default true). */
  rebaseOnSuccess?: boolean
}

export interface FormApi<T extends AnyRecord, R = unknown> {
  values: T
  /** Set one top-level field (typed) — `form.set('price', 1999)`. */
  set: <K extends keyof T>(key: K, value: T[K]) => void
  /** Set a nested path — `form.setPath('seo.title', 'x')`. */
  setPath: (path: string, value: unknown) => void
  /** Merge several fields. */
  setValues: (patch: Partial<T> | ((prev: T) => T)) => void
  /** Props for text inputs/textarea: `<Input {...form.bind('title')} />` (also works with nested paths). */
  bind: (path: string) => { name: string; value: string; onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string) => void }
  errors: Record<string, string>
  /** Error for a field path (for <Field error=…>). */
  error: (path: string) => string | undefined
  setErrors: (errors: Record<string, string>) => void
  /** A non-field error from the last submit — a root error (server `_`, zod `_form`), an error for a
   *  field this form doesn't render, or a non-validation failure. Show it in a Banner; it is also toasted. */
  formError: string | null
  dirty: boolean
  isDirty: (path: string) => boolean
  /** Reset to the baseline, or to new values (e.g. after refetch). */
  reset: (next?: T) => void
  submit: (e?: FormEvent) => Promise<R | undefined>
  submitting: boolean
  /** Spread on <form>: `<form {...form.formProps}>` (onSubmit + noValidate). */
  formProps: { onSubmit: (e: FormEvent) => void; noValidate: true }
}

/**
 * Minimal form state.
 * ```
 * const form = useForm({ initial: { title: p.title, price: p.price }, schema, onSubmit: v => api.patch(`/api/admin/products/${id}`, v), successMessage: 'Product saved' })
 * <form {...form.formProps}><Field label="Title" error={form.error('title')}><Input {...form.bind('title')} /></Field>
 *   <Field label="Price" error={form.error('price')}><MoneyInput value={form.values.price} onChange={v => form.set('price', v)} /></Field></form>
 * <StickySaveBar dirty={form.dirty} saving={form.submitting} onSave={form.submit} onDiscard={() => form.reset()} />
 * ```
 * Server errors: an ApiError with `fields` ({ title: 'Required' }) is mapped onto the fields that
 * render `form.error(path)` and the first invalid control is focused; root errors (`_`, `_form`),
 * errors for fields not on screen and any other failure toast and set `formError` — nothing is
 * dropped silently. Saves are refused while a control shows an invalid draft (MoneyInput "1.2.3").
 */
export function useForm<T extends AnyRecord, R = unknown>(opts: UseFormOptions<T, R>): FormApi<T, R> {
  const [baseline, setBaseline] = useState<T>(opts.initial)
  const [values, setValuesState] = useState<T>(opts.initial)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const attempted = useRef(false)
  const inflight = useRef(false)
  const optsRef = useRef(opts)
  optsRef.current = opts
  const valuesRef = useRef(values)
  valuesRef.current = values
  // Paths this form actually shows errors for (every `form.error(path)` call registers one), so a
  // server error for anything else can be surfaced instead of vanishing.
  const shownPaths = useRef(new Set<string>())

  const update = useCallback((fn: (prev: T) => T, touched?: string) => {
    // Derive from the ref (not a state updater) so rapid successive sets compose and no
    // setState happens inside an updater function.
    const next = fn(valuesRef.current)
    valuesRef.current = next
    setValuesState(next)
    const schema = optsRef.current.schema
    if (attempted.current && schema) setErrors(zodErrors(schema, next))
    else if (touched) setErrors(e => {
      // Editing a field clears its server error.
      if (!(touched in e)) return e
      const { [touched]: _drop, ...rest } = e
      return rest
    })
  }, [])

  const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => update(p => ({ ...p, [key]: value }), String(key)), [update])
  const setPathFn = useCallback((path: string, value: unknown) => update(p => setPath(p, path, value), path), [update])
  const setValues = useCallback((patch: Partial<T> | ((prev: T) => T)) => update(p => (typeof patch === 'function' ? patch(p) : { ...p, ...patch })), [update])

  const bind = useCallback((path: string) => ({
    name: path,
    value: (getPath(valuesRef.current, path) ?? '') as string,
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string) => setPathFn(path, typeof e === 'string' ? e : e.target.value),
  }), [setPathFn])

  const reset = useCallback((next?: T) => {
    const v = next ?? baseline
    if (next) setBaseline(next)
    setValuesState(v); valuesRef.current = v
    setErrors({}); setFormError(null); attempted.current = false
  }, [baseline])

  /** Apply errors: field ones go to the fields, the rest become formError + a toast. */
  const route = useCallback((errs: Record<string, string>, fallback: string | null) => {
    setErrors(errs)
    const { fields, rest } = splitFieldErrors(errs, shownPaths.current)
    setFormError(rest.length ? rest.join(' ') : fallback)
    if (rest.length) toast.error(rest[0], { id: 'form-error' })
    if (Object.keys(fields).length) focusFirstInvalid()
  }, [])

  const submit = useCallback(async (e?: FormEvent): Promise<R | undefined> => {
    e?.preventDefault()
    if (inflight.current) return undefined
    // A control showing text that isn't its value (typo, over max) must be fixed first.
    const formEl = e?.currentTarget instanceof HTMLFormElement ? e.currentTarget : null
    if (blockOnInvalidDraft(formEl ?? undefined)) return undefined
    const o = optsRef.current
    const current = valuesRef.current
    attempted.current = true
    if (o.schema) {
      const errs = zodErrors(o.schema, current)
      if (Object.keys(errs).length) { route(errs, null); return undefined }
      setErrors({})
    }
    inflight.current = true
    setSubmitting(true); setFormError(null)
    try {
      const result = await withReauth(() => Promise.resolve(o.onSubmit(current)))
      if (o.rebaseOnSuccess !== false) setBaseline(current)
      attempted.current = false
      setErrors({})
      if (o.successMessage) toast.success(o.successMessage)
      o.onSuccess?.(result, current)
      return result
    } catch (err) {
      if (err instanceof ApiError && err.code === 'reauth_cancelled') return undefined
      if (err instanceof ApiError && err.fields && typeof err.fields === 'object' && Object.keys(err.fields).length) {
        route(err.fields, err.message && err.code !== 'validation_failed' ? err.message : null)
      } else {
        const msg = err instanceof Error ? err.message : 'Something went wrong.'
        setFormError(msg)
        toast.error(err)
      }
      return undefined
    } finally {
      inflight.current = false
      setSubmitting(false)
    }
  }, [route])

  const dirty = useMemo(() => !deepEqual(values, baseline), [values, baseline])
  const isDirty = useCallback((path: string) => !deepEqual(getPath(values, path), getPath(baseline, path)), [values, baseline])
  const error = useCallback((path: string) => { shownPaths.current.add(path); return errors[path] }, [errors])
  const formProps = useMemo(() => ({ onSubmit: (e: FormEvent) => { void submit(e) }, noValidate: true as const }), [submit])

  return { values, set, setPath: setPathFn, setValues, bind, errors, error, setErrors, formError, dirty, isDirty, reset, submit, submitting, formProps }
}

function focusFirstInvalid() {
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>('[aria-invalid="true"]')
    if (el) { el.focus({ preventScroll: true }); el.scrollIntoView({ block: 'center', behavior: 'smooth' }) }
  })
}
